/**
 * Permissions Context - Phase 11
 * 
 * Provides global permissions state and utilities for RBAC.
 * Loads permissions from /api/admin/me on app boot.
 */
import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import { PERMISSIONS, FEATURE_GATES, ROUTE_PERMISSIONS } from '../constants/permissions';

const PermissionsContext = createContext(null);

export function PermissionsProvider({ children }) {
  const [permissions, setPermissions] = useState([]);
  const [adminProfile, setAdminProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  // Load permissions from backend
  const loadPermissions = useCallback(async (force = false) => {
    // Skip if recently fetched (within 5 minutes) unless forced
    if (!force && lastFetched && Date.now() - lastFetched < 5 * 60 * 1000) {
      return;
    }

    const sessionId = localStorage.getItem('adminSessionId');
    
    if (!sessionId) {
      setPermissions([]);
      setAdminProfile(null);
      setIsLoading(false);
      return;
    }

    try {
      setError(null);
      const response = await api.get('/admin/me');
      
      // Handle axios response - check for data property
      const data = response.data?.data || response.data;
      
      if (data && data.permissions) {
        setPermissions(data.permissions || []);
        setAdminProfile(data);
        setLastFetched(Date.now());
      } else {
        setPermissions([]);
        setAdminProfile(null);
      }
    } catch (err) {
      console.error('Failed to load permissions:', err);
      setError(err.message);
      // Don't clear permissions on error - might be temporary
      if (err.response?.status === 401) {
        setPermissions([]);
        setAdminProfile(null);
      }
    } finally {
      setIsLoading(false);
    }
  }, [lastFetched]);

  // Load on mount and when session changes
  useEffect(() => {
    const sessionId = localStorage.getItem('adminSessionId');
    if (sessionId) {
      loadPermissions(true);
    } else {
      setIsLoading(false);
    }
  }, []);

  // Listen for storage changes (login/logout in other tabs)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'adminSessionId') {
        if (e.newValue) {
          loadPermissions(true);
        } else {
          setPermissions([]);
          setAdminProfile(null);
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [loadPermissions]);

  /**
   * Check if user has a specific permission
   */
  const can = useCallback((permission) => {
    if (!permission) return true;
    return permissions.includes(permission);
  }, [permissions]);

  /**
   * Check if user has ANY of the specified permissions
   */
  const canAny = useCallback((permissionList) => {
    if (!permissionList || permissionList.length === 0) return true;
    return permissionList.some(p => permissions.includes(p));
  }, [permissions]);

  /**
   * Check if user has ALL of the specified permissions
   */
  const canAll = useCallback((permissionList) => {
    if (!permissionList || permissionList.length === 0) return true;
    return permissionList.every(p => permissions.includes(p));
  }, [permissions]);

  /**
   * Check if user can perform a feature action
   */
  const canFeature = useCallback((featureName) => {
    const requiredPerms = FEATURE_GATES[featureName];
    if (!requiredPerms || requiredPerms.length === 0) return true;
    return canAll(requiredPerms);
  }, [canAll]);

  /**
   * Check if user can access a route
   */
  const canAccessRoute = useCallback((path) => {
    // Find matching route pattern
    const routePerms = ROUTE_PERMISSIONS[path];
    if (!routePerms || routePerms.length === 0) return true;
    return canAny(routePerms);
  }, [canAny]);

  /**
   * Get permission info
   */
  const getPermissionInfo = useCallback((permission) => {
    return PERMISSIONS[permission] || { name: permission, category: 'other', risk: 'unknown' };
  }, []);

  /**
   * Get missing permissions for a feature
   */
  const getMissingPermissions = useCallback((featureName) => {
    const requiredPerms = FEATURE_GATES[featureName] || [];
    return requiredPerms.filter(p => !permissions.includes(p));
  }, [permissions]);

  /**
   * Require a permission (throws or returns false)
   */
  const requireCan = useCallback((permission) => {
    if (!can(permission)) {
      const info = getPermissionInfo(permission);
      throw new Error(`Permission denied: ${info.name} (${permission})`);
    }
    return true;
  }, [can, getPermissionInfo]);

  // Memoize context value to prevent unnecessary rerenders
  const value = useMemo(() => ({
    // State
    permissions,
    adminProfile,
    isLoading,
    error,
    role: adminProfile?.role || 'user',
    // Multi-tenant: vendor actor info (null for master admins)
    isVendorActor: !!adminProfile?.is_vendor,
    isImpersonating: !!adminProfile?.is_impersonating,
    vendorId: adminProfile?.vendor_id || null,
    vendor: adminProfile?.vendor || null,
    actorSource: adminProfile?.actor_source || 'admin',
    
    // Utilities
    can,
    canAny,
    canAll,
    canFeature,
    canAccessRoute,
    requireCan,
    getPermissionInfo,
    getMissingPermissions,
    
    // Actions
    refreshPermissions: () => loadPermissions(true),
  }), [
    permissions, adminProfile, isLoading, error,
    can, canAny, canAll, canFeature, canAccessRoute,
    requireCan, getPermissionInfo, getMissingPermissions, loadPermissions
  ]);

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

/**
 * Hook to access permissions context
 */
export function usePermissions() {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
}

/**
 * Hook to check a specific permission
 */
export function useCan(permission) {
  const { can, isLoading } = usePermissions();
  return { allowed: can(permission), isLoading };
}

/**
 * Hook to check a feature gate
 */
export function useCanFeature(featureName) {
  const { canFeature, getMissingPermissions, isLoading } = usePermissions();
  return {
    allowed: canFeature(featureName),
    missingPermissions: getMissingPermissions(featureName),
    isLoading,
  };
}

export default PermissionsProvider;
